import { Reagent, ApiResponse, User } from '../types';
import { getApiPublicBase } from '../utils/apiPublicBase';

const REAGENT_API_PREFIX = '/api/reagents';
const INSTRUMENT_API_PREFIX = '/api/instruments';
const RESERVATION_API_PREFIX = '/api/reservations';
const MEMBER_API_PREFIX = '/api/members';

function instrumentAuthHeaders(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** 未登录浏览仪器列表时附带保存的团队编码 */
function withTeamQuery(url: string): string {
  if (typeof window === 'undefined') return url;
  const token = localStorage.getItem('token');
  if (token) return url;
  const teamCode = localStorage.getItem('teamCode');
  if (!teamCode) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}teamCode=${encodeURIComponent(teamCode)}`;
}

// 试剂相关API
export async function getReagents(): Promise<ApiResponse<Reagent[]>> {
  const token = localStorage.getItem('token');
  const res = await fetch(REAGENT_API_PREFIX, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function addReagent(data: Partial<Reagent>): Promise<ApiResponse<Reagent>> {
  const token = localStorage.getItem('token');
  const res = await fetch(REAGENT_API_PREFIX, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function updateReagent(id: string, data: Partial<Reagent>): Promise<ApiResponse<Reagent>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${REAGENT_API_PREFIX}/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function deleteReagent(id: string): Promise<ApiResponse<Reagent>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${REAGENT_API_PREFIX}/${id}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function getReagentStockAccess(): Promise<ApiResponse<{ canManage: boolean }>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${REAGENT_API_PREFIX}/stock-access`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export type RestockHistoryRecord = {
  applicationId: string;
  projectIndex: number;
  title?: string;
  applicant?: string;
  unitPrice: number | null;
  quantity: number | null;
  total: number | null;
  unit?: string;
  approvedAt?: string | null;
};

export type RestockHistoryResponse = {
  records: RestockHistoryRecord[];
  latestApprovedUnitPrice: number | null;
  currentUnitPrice: number | null;
};

export async function getReagentRestockHistory(
  id: string
): Promise<ApiResponse<RestockHistoryResponse>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${REAGENT_API_PREFIX}/${id}/restock-history`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export type ClaimHistoryRecord = {
  id: string;
  claimant: string;
  quantity: number | null;
  unit?: string;
  reason?: string;
  priority?: string;
  claimedAt?: string | null;
  message?: string;
};

export async function getReagentClaimHistory(
  id: string
): Promise<ApiResponse<{ records: ClaimHistoryRecord[] }>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${REAGENT_API_PREFIX}/${id}/claim-history`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

const CONSUMABLE_API_PREFIX = '/api/consumables';

export async function getConsumableStockAccess(): Promise<ApiResponse<{ canManage: boolean }>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${CONSUMABLE_API_PREFIX}/stock-access`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function getConsumableRestockHistory(
  id: string
): Promise<ApiResponse<RestockHistoryResponse>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${CONSUMABLE_API_PREFIX}/${id}/restock-history`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function getConsumableClaimHistory(
  id: string
): Promise<ApiResponse<{ records: ClaimHistoryRecord[] }>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${CONSUMABLE_API_PREFIX}/${id}/claim-history`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export interface ConsumableItem {
  key?: string;
  _id?: string;
  image?: string;
  name: string;
  model?: string;
  unit: string;
  quantity: number;
  location?: string;
  remark?: string;
  buyDate?: string;
  receiver?: string;
  restockThreshold?: number;
}

export async function getConsumables(): Promise<ConsumableItem[]> {
  const token = localStorage.getItem('token');
  const res = await fetch(CONSUMABLE_API_PREFIX, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

// 仪器相关API
export interface TimeSlot {
  dayOfWeek: number; // 0=周日, 1=周一, ..., 6=周六
  startTime: string; // "09:00"
  endTime: string; // "18:00"
  isEnabled: boolean;
}

export interface EndUsePhotoGroup {
  key: string;
  label: string;
  required?: boolean;
  maxPhotos?: number;
}

export interface EndUseRecordPhotoGroup {
  groupKey: string;
  label: string;
  urls: string[];
}

export interface EndUseRecord {
  submittedAt?: string;
  notes?: string;
  photoGroups?: EndUseRecordPhotoGroup[];
}

export interface ReservationRules {
  maxDuration: number;
  minDuration: number;
  advanceBookingDays: number;
  availableTimeSlots: TimeSlot[];
  requiresApproval: boolean;
  maxConcurrentUsers: number;
  endUseStatusEnabled?: boolean;
  endUsePhotoGroups?: EndUsePhotoGroup[];
}

export interface Instrument {
  _id?: string;
  name: string;
  model?: string;
  manufacturer?: string;
  location?: string;
  status: '可用' | '使用中' | '维护中' | '故障' | '报废';
  category?: string;
  description?: string;
  purchaseDate?: string;
  warrantyExpiry?: string;
  /** 维护周期天数；未设置表示无 */
  maintenanceCycleDays?: number | null;
  maintenanceCycleStartDate?: string | null;
  responsiblePerson?: string;
  /** 可预约成员（顿号分隔姓名）；空表示团队全部成员可用 */
  allowedUsers?: string;
  image?: string;
  /** @deprecated 旧版富文本内嵌说明 */
  usageInstructions?: string;
  /** 关联的知识库文档 ID，或 populate 后的 { _id, title } */
  usageInstructionKnowledgeBaseId?: string | { _id: string; title?: string };
  isActive?: boolean;
  reservationRules?: ReservationRules;
  createdAt?: string;
  updatedAt?: string;
}

// 使用统一的User接口替代Member接口

export async function getInstruments(): Promise<ApiResponse<Instrument[]>> {
  const res = await fetch(withTeamQuery(INSTRUMENT_API_PREFIX), { headers: { ...instrumentAuthHeaders() } });
  return res.json();
}

export async function getInstrumentsPaginated(params?: { page?: number; pageSize?: number; managedOnly?: boolean }): Promise<ApiResponse<{ items: Instrument[]; total: number; page: number; pageSize: number }>> {
  const query = new URLSearchParams();
  if (params?.page) query.set('page', String(params.page));
  if (params?.pageSize) query.set('pageSize', String(params.pageSize));
  if (params?.managedOnly) query.set('managedOnly', 'true');
  let url = `${INSTRUMENT_API_PREFIX}/paginated${query.toString() ? `?${query.toString()}` : ''}`;
  url = withTeamQuery(url);
  const res = await fetch(url, { headers: { ...instrumentAuthHeaders() } });
  return res.json();
}

export async function getInstrumentById(id: string): Promise<ApiResponse<Instrument>> {
  const url = withTeamQuery(`${INSTRUMENT_API_PREFIX}/${id}`);
  const res = await fetch(url, { headers: { ...instrumentAuthHeaders() } });
  return res.json();
}

export async function searchInstruments(query: string): Promise<ApiResponse<Instrument[]>> {
  const url = withTeamQuery(`${INSTRUMENT_API_PREFIX}/search?query=${encodeURIComponent(query)}`);
  const res = await fetch(url, { headers: { ...instrumentAuthHeaders() } });
  return res.json();
}

export async function getInstrumentsByStatus(status: string): Promise<ApiResponse<Instrument[]>> {
  const url = withTeamQuery(`${INSTRUMENT_API_PREFIX}/status/${status}`);
  const res = await fetch(url, { headers: { ...instrumentAuthHeaders() } });
  return res.json();
}

export async function addInstrument(data: Partial<Instrument>): Promise<ApiResponse<Instrument>> {
  const token = localStorage.getItem('token');
  const res = await fetch(INSTRUMENT_API_PREFIX, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function updateInstrument(id: string, data: Partial<Instrument>): Promise<ApiResponse<Instrument>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${INSTRUMENT_API_PREFIX}/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function deleteInstrument(id: string): Promise<ApiResponse<Instrument>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${INSTRUMENT_API_PREFIX}/${id}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export interface InstrumentMaintenanceRecord {
  _id?: string;
  instrumentId?: string;
  /** 维护人（顿号分隔姓名） */
  maintainers?: string;
  content: string;
  performedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string | { _id: string; name?: string; username?: string };
}

export async function getInstrumentMaintenanceRecords(
  instrumentId: string
): Promise<ApiResponse<InstrumentMaintenanceRecord[]>> {
  const res = await fetch(`${INSTRUMENT_API_PREFIX}/${instrumentId}/maintenance-records`, {
    headers: { ...instrumentAuthHeaders() }
  });
  return res.json();
}

export async function addInstrumentMaintenanceRecord(
  instrumentId: string,
  data: { content: string; performedAt?: string; maintainers: string }
): Promise<ApiResponse<InstrumentMaintenanceRecord>> {
  const res = await fetch(`${INSTRUMENT_API_PREFIX}/${instrumentId}/maintenance-records`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...instrumentAuthHeaders()
    },
    body: JSON.stringify(data)
  });
  return res.json();
}

// 预约相关API
export interface Reservation {
  _id?: string;
  instrumentId: string | Instrument;
  userId: string | { _id: string; name: string; username: string; email: string };
  startTime: string;
  endTime: string;
  purpose: string;
  status: '待审核' | '已批准' | '已拒绝' | '使用中' | '已完成' | '已取消';
  approvedBy?: string | { _id: string; name: string; username: string };
  approvedAt?: string;
  approvalComment?: string;
  actualStartTime?: string;
  actualEndTime?: string;
  usageNotes?: string;
  endUseRecord?: EndUseRecord;
  isRecurring?: boolean;
  recurringPattern?: string;
  recurringEndDate?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** 占用时段、参与冲突校验的预约状态（与后端 overlap 查询一致） */
export const OCCUPYING_RESERVATION_STATUSES: Reservation['status'][] = ['待审核', '已批准', '使用中'];

export function isOccupyingReservation(r: Pick<Reservation, 'status'>): boolean {
  return OCCUPYING_RESERVATION_STATUSES.includes(r.status);
}

export interface UsageStats {
  period: 'week' | 'month' | 'year';
  range: { start: string; end: string };
  totalDurationMinutes: number;
  totalCount: number;
  averageDurationMinutes: number;
  instrumentBreakdown: Array<{
    instrumentId: string;
    instrumentName: string;
    durationMinutes: number;
    count: number;
  }>;
  timeline: Array<{
    date: string;
    durationMinutes: number;
    count: number;
  }>;
}

export async function getReservations(): Promise<ApiResponse<Reservation[]>> {
  const token = localStorage.getItem('token');
  const res = await fetch(RESERVATION_API_PREFIX, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function getMyReservations(): Promise<ApiResponse<Reservation[]>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${RESERVATION_API_PREFIX}/my`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function getMyRecentReservations(): Promise<ApiResponse<Reservation[]>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${RESERVATION_API_PREFIX}/my/recent`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function getMyUsageStats(params: {
  period?: 'week' | 'month' | 'year';
  startDate?: string;
  endDate?: string;
}): Promise<ApiResponse<UsageStats>> {
  const token = localStorage.getItem('token');
  const query = new URLSearchParams();
  if (params.period) query.append('period', params.period);
  if (params.startDate) query.append('startDate', params.startDate);
  if (params.endDate) query.append('endDate', params.endDate);

  const res = await fetch(`${RESERVATION_API_PREFIX}/my/usage?${query.toString()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function getInstrumentReservations(instrumentId: string): Promise<ApiResponse<Reservation[]>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${RESERVATION_API_PREFIX}/instrument/${instrumentId}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function checkTimeConflict(instrumentId: string, startTime: string, endTime: string, excludeReservationId?: string): Promise<ApiResponse<{ hasConflict: boolean; conflictingReservation: Reservation | null }>> {
  const token = localStorage.getItem('token');
  const params = new URLSearchParams({
    instrumentId,
    startTime,
    endTime,
    ...(excludeReservationId && { excludeReservationId })
  });
  const res = await fetch(`${RESERVATION_API_PREFIX}/check-conflict?${params}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function createReservation(data: Partial<Reservation>): Promise<ApiResponse<Reservation>> {
  const token = localStorage.getItem('token');
  const res = await fetch(RESERVATION_API_PREFIX, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function updateReservation(id: string, data: Partial<Reservation>): Promise<ApiResponse<Reservation>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${RESERVATION_API_PREFIX}/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function approveReservation(id: string, status: string, approvalComment?: string): Promise<ApiResponse<Reservation>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${RESERVATION_API_PREFIX}/${id}/approve`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ status, approvalComment }),
  });
  return res.json();
}

export async function startUsage(reservationId: string, actualStartTime?: string): Promise<ApiResponse<Reservation>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${RESERVATION_API_PREFIX}/${reservationId}/start`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ actualStartTime }),
  });
  return res.json();
}

export async function endUsage(
  reservationId: string,
  options?: { actualEndTime?: string; usageNotes?: string; endUseRecord?: EndUseRecord }
): Promise<ApiResponse<Reservation>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${RESERVATION_API_PREFIX}/${reservationId}/end`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(options || {}),
  });
  return res.json();
}

export async function submitEndUseRecord(
  reservationId: string,
  endUseRecord: { notes?: string; photoGroups: EndUseRecordPhotoGroup[] }
): Promise<ApiResponse<Reservation>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${RESERVATION_API_PREFIX}/${reservationId}/end-use-record`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ endUseRecord }),
  });
  return res.json();
}

// 取消预约（开始前，预约人可取消）
export async function cancelReservation(id: string): Promise<ApiResponse<Reservation>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${RESERVATION_API_PREFIX}/${id}/cancel`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  });
  return res.json();
}

export async function deleteReservation(id: string): Promise<ApiResponse<Reservation>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${RESERVATION_API_PREFIX}/${id}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export interface ApprovalApproverSettings {
  consumable: string[];
  reagent: string[];
  reagentRequireSupervisorApproval?: boolean;
  seal: string[];
  repair: string[];
  leave: string[];
  test: string[];
  businessTrip: string[];
  itemClaim: string[];
  financeReimbursement: string[];
}

const APPROVAL_API_PREFIX = '/api/approvals';

export async function getApprovalApproverSettings(): Promise<ApiResponse<ApprovalApproverSettings>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${APPROVAL_API_PREFIX}/approver-settings`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function updateApprovalApproverSettings(
  settings: ApprovalApproverSettings
): Promise<ApiResponse<ApprovalApproverSettings>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${APPROVAL_API_PREFIX}/approver-settings`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ settings })
  });
  return res.json();
}

export interface ProcurementStatusRecord {
  status: '待采购' | '已采购' | '已到货' | '已入库' | '已处理' | '不补货';
  /** projectIndex -> 行状态；缺省回退到 status */
  projectStatuses?: Record<string, string>;
  pickupInfo?: {
    needsPickup: boolean;
    pickupLocation: string;
    pickupNumber: string;
  };
  storageLocations?: Record<string, string>;
  /** projectIndex -> 收货照片 URL 列表 */
  receiptPhotos?: Record<string, string[]>;
  followUpApplicationId?: string | null;
  reminderClearedAt?: string | null;
}

export async function uploadApprovalAttachmentFile(
  file: File
): Promise<ApiResponse<{ url: string }>> {
  const token = localStorage.getItem('token');
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch('/api/upload/approval-attachment', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { success: false, error: (data as { error?: string }).error || '上传失败' };
  }
  if (!data?.url) {
    return { success: false, error: '上传失败' };
  }
  return { success: true, data: { url: data.url } };
}

/** 确认收货照片：走通用图片上传（支持 HEIC→JPG） */
export async function uploadReceiptPhoto(file: File): Promise<ApiResponse<{ url: string }>> {
  const token = localStorage.getItem('token');
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch('/api/upload', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { success: false, error: (data as { error?: string }).error || '上传失败' };
  }
  if (!data?.url) {
    return { success: false, error: '上传失败' };
  }
  return { success: true, data: { url: data.url as string } };
}

export async function getFinanceReimbursementApplications(): Promise<ApiResponse<any[]>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${APPROVAL_API_PREFIX}/finance-reimbursements`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function getProcurementApplications(
  category: '耗材申请' | '试剂申请'
): Promise<ApiResponse<any[]>> {
  const token = localStorage.getItem('token');
  const query = new URLSearchParams({ category });
  const res = await fetch(`${APPROVAL_API_PREFIX}/procurement-applications?${query.toString()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function getProcurementStatusMap(
  category: '耗材申请' | '试剂申请'
): Promise<ApiResponse<Record<string, ProcurementStatusRecord>>> {
  const token = localStorage.getItem('token');
  const query = new URLSearchParams({ category });
  const res = await fetch(`${APPROVAL_API_PREFIX}/procurement-status?${query.toString()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function updateProcurementStatus(
  applicationId: string,
  payload: {
    category: '耗材申请' | '试剂申请';
    status: '待采购' | '已采购' | '已到货' | '已入库' | '已处理';
    /** 手动确认已处理：立即清除低库存提醒 */
    clearReminder?: boolean;
    pickupInfo?: {
      needsPickup: boolean;
      pickupLocation: string;
      pickupNumber: string;
    };
    projectIndex?: number;
    storageLocation?: string;
    receiptPhotoUrls?: string[];
  }
): Promise<ApiResponse<ProcurementStatusRecord>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${APPROVAL_API_PREFIX}/procurement-status/${applicationId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(payload)
  });
  return res.json();
}

export async function updateReagentProject(
  applicationId: string,
  projectIndex: number,
  patch: {
    category?: string;
    name?: string;
    cas?: string;
    catalogNumber?: string;
    vendor?: string;
    specification?: string;
    model?: string;
    unit?: string;
    purchaseChannel?: string;
    amount?: string;
    price?: string;
    usage?: string;
    image?: string;
  }
): Promise<ApiResponse<{ reagentProjects: any[]; totalAmount: number }>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${APPROVAL_API_PREFIX}/${applicationId}/reagent-project/${projectIndex}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(patch)
  });
  return res.json();
}

export async function updateConsumableProject(
  applicationId: string,
  projectIndex: number,
  patch: {
    category?: string;
    name?: string;
    model?: string;
    brand?: string;
    unit?: string;
    purchaseChannel?: string;
    purchaseLink?: string;
    supplier?: string;
    amount?: string;
    price?: string;
    usage?: string;
    image?: string;
  }
): Promise<ApiResponse<{ consumableProjects: any[]; totalAmount: number }>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${APPROVAL_API_PREFIX}/${applicationId}/consumable-project/${projectIndex}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(patch)
  });
  return res.json();
}

export async function confirmPlatformOrderCompleted(
  applicationId: string
): Promise<ApiResponse<{ completed: boolean; price?: number | null; link?: string }>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${APPROVAL_API_PREFIX}/${applicationId}/platform-order-complete`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  });
  return res.json();
}

export async function batchUpdateProcurementStatus(
  payload: {
    category: '耗材申请' | '试剂申请';
    applicationIds: string[];
    status: '待采购' | '已采购' | '已到货' | '已入库' | '已处理';
  }
): Promise<ApiResponse<unknown>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${APPROVAL_API_PREFIX}/procurement-status/batch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(payload)
  });
  return res.json();
}

export async function submitApprovalApplication(data: Record<string, unknown>): Promise<ApiResponse<any>> {
  const token = localStorage.getItem('token');
  const res = await fetch('/api/approvals', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(data)
  });
  return res.json();
}

export async function getMyApprovalApplications(): Promise<ApiResponse<any[]>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${APPROVAL_API_PREFIX}/my-applications`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export type SavedShippingAddress = {
  _id: string;
  recipientName: string;
  recipientPhone: string;
  address: string;
  isDefault: boolean;
  lastUsedAt?: string | null;
};

export async function getShippingAddresses(): Promise<
  ApiResponse<{
    addresses: SavedShippingAddress[];
    profileDefaults: { recipientName: string; recipientPhone: string };
  }>
> {
  const token = localStorage.getItem('token');
  const res = await fetch('/api/auth/shipping-addresses', {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function setDefaultShippingAddress(id: string): Promise<ApiResponse<unknown>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`/api/auth/shipping-addresses/${id}/default`, {
    method: 'PUT',
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function deleteShippingAddress(id: string): Promise<ApiResponse<unknown>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`/api/auth/shipping-addresses/${id}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function getPendingApprovalApplications(): Promise<ApiResponse<any[]>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${APPROVAL_API_PREFIX}/pending`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function getProcessedApprovalApplications(): Promise<ApiResponse<any[]>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${APPROVAL_API_PREFIX}/processed`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function approveApprovalApplication(
  id: string,
  payload: { result: '通过' | '驳回'; comment?: string }
): Promise<ApiResponse<any>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${APPROVAL_API_PREFIX}/${id}/approve`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(payload)
  });
  return res.json();
}

export async function confirmMerchantPrice(
  id: string,
  payload: { result: '通过' | '驳回'; comment?: string }
): Promise<ApiResponse<any>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${APPROVAL_API_PREFIX}/${id}/confirm-merchant-price`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(payload)
  });
  return res.json();
}

export async function getAuthorizedMerchants(): Promise<ApiResponse<Array<{ _id: string; name: string }>>> {
  const token = localStorage.getItem('token');
  const res = await fetch('/api/merchant/authorized', {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function withdrawApprovalApplication(id: string): Promise<ApiResponse<any>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${APPROVAL_API_PREFIX}/${id}/withdraw`, {
    method: 'PUT',
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

export async function submitConsumableClaim(
  consumableId: string,
  payload: { amount: number; reason: string; priority?: '普通' | '紧急' | '特急' }
): Promise<ApiResponse<any>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`/api/consumables/${consumableId}/claim`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(payload)
  });
  return res.json();
}

export async function submitReagentClaim(
  reagentId: string,
  payload: { amount: number; reason: string; priority?: '普通' | '紧急' | '特急' }
): Promise<ApiResponse<any>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`/api/reagents/${reagentId}/claim`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(payload)
  });
  return res.json();
}

export async function stockInReagentFromApproval(
  applicationId: string,
  payload: { projectIndex: number; location: string; receiver?: string }
): Promise<ApiResponse<any>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`/api/reagents/stock-in-from-approval/${applicationId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(payload)
  });
  return res.json();
}

export async function stockInConsumableFromApproval(
  applicationId: string,
  payload: { projectIndex: number; location: string; receiver?: string }
): Promise<ApiResponse<any>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`/api/consumables/stock-in-from-approval/${applicationId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(payload)
  });
  return res.json();
}

// 通知相关API
export interface Notification {
  _id: string;
  recipient: string;
  sender?: {
    _id: string;
    name: string;
    username: string;
  };
  title: string;
  content: string;
  type:
    | 'system'
    | 'reservation'
    | 'approval'
    | 'maintenance'
    | 'training'
    | 'equipment'
    | 'announcement'
    | 'feedback'
    | 'lab_activity'
    | 'shared_folder_invite'
    | 'shared_folder_accepted'
    | 'shared_folder_rejected'
    | 'shared_experiment_notebook_invite'
    | 'shared_experiment_notebook_accepted'
    | 'shared_experiment_notebook_rejected';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  isRead: boolean;
  readAt?: string;
  /** 文献/实验共享邀请等使用的处理状态（与 isRead 并存） */
  status?: 'pending' | 'accepted' | 'rejected' | 'read' | null;
  relatedResource?: string;
  relatedResourceType?:
    | 'reservation'
    | 'instrument'
    | 'user'
    | 'maintenance'
    | 'shared_folder'
    | 'shared_experiment_notebook'
    | 'feedback_post'
    | 'feedback_report'
    | 'lab_activity';
  folder?: { _id: string; name?: string };
  experimentNotebookFolder?: { _id: string; name?: string };
  actionUrl?: string;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
}

const NOTIFICATION_API_PREFIX = '/api/notifications';

export async function getNotifications(params?: {
  page?: number;
  limit?: number;
  type?: string;
  isRead?: boolean;
}): Promise<ApiResponse<{ notifications: Notification[]; pagination: any }>> {
  const token = localStorage.getItem('token');
  const queryParams = new URLSearchParams();
  
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.type) queryParams.append('type', params.type);
  if (params?.isRead !== undefined) queryParams.append('isRead', params.isRead.toString());
  
  const url = `${NOTIFICATION_API_PREFIX}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  const res = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
  });
  return res.json();
}

export async function getUnreadCount(): Promise<ApiResponse<{ count: number }>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${NOTIFICATION_API_PREFIX}/unread-count`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
  });
  return res.json();
}

export async function markNotificationAsRead(id: string): Promise<ApiResponse<Notification>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${NOTIFICATION_API_PREFIX}/${id}/read`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
  });
  return res.json();
}

// 成员相关API
export async function changePassword(oldPassword: string, newPassword: string): Promise<ApiResponse<{message: string}>> {
  const token = localStorage.getItem('token');
  const res = await fetch('/api/auth/change-password', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ oldPassword, newPassword }),
  });
  
  if (!res.ok) {
    const error = await res.json();
    return { success: false, error: error.error || '密码修改失败' };
  }
  
  const result = await res.json();
  return { success: true, data: result };
}

export async function getCurrentUser(): Promise<ApiResponse<User>> {
  const token = localStorage.getItem('token');
  try {
    const res = await fetch('/api/auth/me', {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { success: false, error: (body && body.error) || '获取用户信息失败' };
    }
    return { success: true, data: body };
  } catch (e) {
    return { success: false, error: '网络或服务异常' };
  }
}

export async function getMembers(): Promise<ApiResponse<User[]>> {
  const token = localStorage.getItem('token');
  const res = await fetch(MEMBER_API_PREFIX, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  });
  
  if (!res.ok) {
    const error = await res.json();
    return { success: false, error: error.error || '获取成员列表失败' };
  }
  
  const members = await res.json();
  return { success: true, data: members };
}

export interface CurrentLeaveMember {
  type?: 'leave' | 'trip';
  leaveStart?: string;
  leaveEnd?: string;
  applicant?: string;
}

export async function getCurrentLeaveMembers(): Promise<ApiResponse<Record<string, CurrentLeaveMember>>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${MEMBER_API_PREFIX}/leave-status/current`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  });
  return res.json();
}

export async function getMemberById(id: string): Promise<ApiResponse<User>> {
  const res = await fetch(`${MEMBER_API_PREFIX}/${id}`);
  return res.json();
}

export async function createMember(data: Partial<User>, avatarFile?: File): Promise<ApiResponse<User>> {
  const token = localStorage.getItem('token');
  
  // 如果有文件上传，使用FormData
  if (avatarFile) {
    const formData = new FormData();
    
    // 添加所有字段到FormData
    Object.keys(data).forEach(key => {
      if (data[key as keyof User] !== undefined && data[key as keyof User] !== null) {
        formData.append(key, String(data[key as keyof User]));
      }
    });
    
    // 添加头像文件
    formData.append('avatar', avatarFile);
    
    const res = await fetch(MEMBER_API_PREFIX, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
        // 注意：不设置Content-Type，让浏览器自动设置multipart/form-data
      },
      body: formData,
    });
    
    if (!res.ok) {
      const error = await res.json();
      return { success: false, error: error.error || '创建成员失败' };
    }
    
    const userData = await res.json();
    return { success: true, data: userData };
  } else {
    // 没有文件上传，使用JSON
    const res = await fetch(MEMBER_API_PREFIX, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(data),
    });
    
    if (!res.ok) {
      const error = await res.json();
      return { success: false, error: error.error || '创建成员失败' };
    }
    
    const userData = await res.json();
    return { success: true, data: userData };
  }
}

export async function updateMember(id: string, data: Partial<User>, avatarFile?: File): Promise<ApiResponse<User>> {
  const token = localStorage.getItem('token');
  
  // 如果有文件上传，使用FormData
  if (avatarFile) {
    const formData = new FormData();
    
    // 添加所有字段到FormData
    Object.keys(data).forEach(key => {
      if (data[key as keyof User] !== undefined && data[key as keyof User] !== null) {
        formData.append(key, String(data[key as keyof User]));
      }
    });
    
    // 添加头像文件
    formData.append('avatar', avatarFile);
    
    const res = await fetch(`${MEMBER_API_PREFIX}/${id}`, {
      method: 'PUT',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
        // 注意：不设置Content-Type，让浏览器自动设置multipart/form-data
      },
      body: formData,
    });
    
    if (!res.ok) {
      const error = await res.json();
      return { success: false, error: error.error || '更新成员失败' };
    }
    
    const userData = await res.json();
    return { success: true, data: userData };
  } else {
    // 没有文件上传，使用JSON
    const res = await fetch(`${MEMBER_API_PREFIX}/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(data),
    });
    
    if (!res.ok) {
      const error = await res.json();
      return { success: false, error: error.error || '更新成员失败' };
    }
    
    const userData = await res.json();
    return { success: true, data: userData };
  }
}

export async function deleteMember(id: string): Promise<ApiResponse<{message: string, deletedAt: string, permanentDeleteDate: string}>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${MEMBER_API_PREFIX}/${id}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  return res.json();
}

// 更新当前用户信息（个人中心使用）
export async function updateProfile(data: Partial<User>, avatarFile?: File): Promise<ApiResponse<User>> {
  const token = localStorage.getItem('token');
  
  // 如果有文件上传，使用FormData
  if (avatarFile) {
    const formData = new FormData();
    
    // 添加所有字段到FormData
    Object.keys(data).forEach(key => {
      if (data[key as keyof User] !== undefined && data[key as keyof User] !== null) {
        formData.append(key, String(data[key as keyof User]));
      }
    });
    
    // 添加头像文件
    formData.append('avatar', avatarFile);
    
    const res = await fetch('/api/auth/update-profile', {
      method: 'PUT',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
        // 注意：不设置Content-Type，让浏览器自动设置multipart/form-data
      },
      body: formData,
    });
    
    if (!res.ok) {
      const error = await res.json();
      return { success: false, error: error.error || '更新信息失败' };
    }
    
    const userData = await res.json();
    return userData;
  } else {
    // 没有文件上传，使用JSON
    const res = await fetch('/api/auth/update-profile', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(data),
    });
    
    if (!res.ok) {
      const error = await res.json();
      return { success: false, error: error.error || '更新信息失败' };
    }
    
    const userData = await res.json();
    return userData;
  }
}

export async function markAllNotificationsAsRead(): Promise<ApiResponse<any>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${NOTIFICATION_API_PREFIX}/mark-all-read`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
  });
  return res.json();
}

export async function deleteNotification(id: string): Promise<ApiResponse<any>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${NOTIFICATION_API_PREFIX}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
  });
  const data = (await res.json().catch(() => ({}))) as ApiResponse<any> & { message?: string };
  if (!res.ok || data.success === false) {
    const err =
      (typeof data.error === 'string' && data.error) ||
      (data as any).message ||
      (typeof data.error === 'object' && data.error?.message) ||
      '删除通知失败';
    return { success: false, error: err };
  }
  return { ...data, success: true };
}

export async function deleteAllNotifications(): Promise<ApiResponse<{ deletedCount?: number }>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${NOTIFICATION_API_PREFIX}/all`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
  });
  const data = (await res.json().catch(() => ({}))) as ApiResponse<{ deletedCount?: number }> & { message?: string };
  if (!res.ok || data.success === false) {
    const err =
      (typeof data.error === 'string' && data.error) ||
      (data as any).message ||
      (typeof data.error === 'object' && data.error?.message) ||
      '删除全部通知失败';
    return { success: false, error: err };
  }
  return { ...data, success: true };
}

// 通知偏好设置相关API
export interface NotificationPreferences {
  instrument: {
    enabled: boolean;
    emailEnabled: boolean;
    scenarios: {
      reservationCreated: boolean;
      reservationApproved: boolean;
      reservationRejected: boolean;
      reservationReminder: boolean;
      reservationCancelled: boolean;
      reservationStatusChanged: boolean;
      maintenanceDue: boolean;
    };
  };
  literature: {
    enabled: boolean;
    emailEnabled: boolean;
    scenarios: {
      sharedFolderInvite: boolean;
      sharedFolderAccepted: boolean;
      sharedFolderRejected: boolean;
      fileShared: boolean;
      quotaWarning: boolean;
    };
  };
  consumable: {
    enabled: boolean;
    emailEnabled: boolean;
    scenarios: {
      lowStock: boolean;
      orderCreated: boolean;
      orderApproved: boolean;
      orderRejected: boolean;
    };
  };
  approval: {
    enabled: boolean;
    emailEnabled: boolean;
    scenarios: {
      applicationSubmitted: boolean;
      applicationApproved: boolean;
      applicationRejected: boolean;
      pendingApproval: boolean;
    };
  };
  feedback: {
    enabled: boolean;
    emailEnabled: boolean;
    scenarios: {
      newPost: boolean;
      officialReply: boolean;
      statusAccepted: boolean;
      statusInProgress: boolean;
      statusResolved: boolean;
      statusRejected: boolean;
      reportProcessed: boolean;
      weeklySummary: boolean;
    };
  };
}

/**
 * 与仪表盘、通知等接口一致的前缀。
 * - localhost:3000 开发：REACT_APP_API_BASE 或默认 http://127.0.0.1:5055
 * - 公网访问：若构建时 REACT_APP_API_BASE 指向 loopback，强制改为 ''，走同源 /api（Nginx 反代）
 * - 可显式设置 REACT_APP_API_BASE 为公网 API 域名（非 loopback）以直连独立 API 域
 */
export const API_FETCH_BASE = getApiPublicBase();

const API_BASE = API_FETCH_BASE;

export async function getNotificationPreferences(): Promise<ApiResponse<NotificationPreferences>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/auth/notification-preferences`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
  });
  
  if (!res.ok) {
    const error = await res.json();
    return { success: false, error: error.error || '获取通知偏好设置失败' };
  }
  
  return res.json();
}

export async function updateNotificationPreferences(preferences: NotificationPreferences): Promise<ApiResponse<NotificationPreferences>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/auth/notification-preferences`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ notificationPreferences: preferences }),
  });
  
  if (!res.ok) {
    const error = await res.json();
    return { success: false, error: error.error || '更新通知偏好设置失败' };
  }
  
  return res.json();
}

// 活动日志相关API
export interface ActivityLog {
  id: string;
  type: 'member' | 'instrument' | 'consumable' | 'reagent' | 'lab-activity';
  action: string;
  message: string;
  time: string;
  actor: {
    id?: string;
    name: string;
    username: string;
  };
  relatedResource?: {
    type: string;
    id: string;
  };
  metadata?: any;
  priority: 'high' | 'medium' | 'low';
}

export async function getRecentActivities(limit?: number): Promise<ApiResponse<ActivityLog[]>> {
  const token = localStorage.getItem('token');
  const url = limit ? `/api/activities/recent?limit=${limit}` : '/api/activities/recent';
  const res = await fetch(`${API_BASE}${url}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
  });
  
  if (!res.ok) {
    const error = await res.json();
    return { success: false, error: error.error || '获取最近活动失败' };
  }
  
  return res.json();
}

// 统计数据相关API
export interface DashboardStats {
  totalMembers: number;
  activeInstruments: number;
}

export async function getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/statistics/dashboard`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
  });
  
  if (!res.ok) {
    const error = await res.json();
    return { success: false, error: error.error || '获取统计数据失败' };
  }
  
  return res.json();
}

// 实验室活动（发起活动）相关 API
export interface LabActivityCreatedBy {
  _id: string;
  name: string;
  username?: string;
}

export interface LabActivityItem {
  _id: string;
  title: string;
  description?: string;
  location?: string;
  startTime: string;
  endTime?: string;
  createdBy: LabActivityCreatedBy | string;
  status: 'published' | 'cancelled';
  createdAt: string;
  /** 报名人数上限；不设表示不限制 */
  maxParticipants?: number;
  registrationPendingCount?: number;
  registrationApprovedCount?: number;
  /** 待审核 + 已通过（计入人数上限） */
  registrationActiveCount?: number;
  /** 是否已满员（仅当设置了人数上限时有效） */
  isRegistrationFull?: boolean;
  /** 当前用户在该活动的报名状态（仅详情接口返回） */
  myRegistration?: { status: 'pending' | 'approved' | 'rejected' };
}

export interface LabActivityRegistrationItem {
  _id: string;
  activity: string | LabActivityItem;
  user: { _id: string; name: string; username?: string; studentId?: string };
  status: 'pending' | 'approved' | 'rejected';
  reviewedAt?: string;
  reviewedBy?: { _id: string; name: string; username?: string };
  rejectReason?: string;
  createdAt: string;
}

export async function createLabActivity(data: {
  title: string;
  description?: string;
  location?: string;
  startTime: string;
  endTime?: string;
  maxParticipants?: number;
}): Promise<ApiResponse<LabActivityItem>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/lab-activities`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(data)
  });
  const json = await res.json();
  if (!res.ok) return { success: false, error: json.error || '发布失败' };
  return json;
}

export async function getLabActivityList(params: {
  tab?: 'all' | 'mine' | 'registered';
  page?: number;
  limit?: number;
}): Promise<ApiResponse<{ list: LabActivityItem[]; pagination: { current: number; pageSize: number; total: number } }>> {
  const token = localStorage.getItem('token');
  const q = new URLSearchParams();
  if (params.tab) q.set('tab', params.tab);
  if (params.page != null) q.set('page', String(params.page));
  if (params.limit != null) q.set('limit', String(params.limit));
  const res = await fetch(`${API_BASE}/api/lab-activities?${q}`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
  });
  const json = await res.json();
  if (!res.ok) return { success: false, error: json.error || '获取列表失败' };
  return json;
}

export async function getLabActivityById(id: string): Promise<ApiResponse<LabActivityItem>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/lab-activities/${id}`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
  });
  const json = await res.json();
  if (!res.ok) return { success: false, error: json.error || '获取活动失败' };
  return json;
}

/** 发起人取消活动（标记为已取消） */
export async function cancelLabActivity(activityId: string): Promise<ApiResponse<LabActivityItem>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/lab-activities/${encodeURIComponent(activityId)}/cancel`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  });
  const json = await res.json();
  if (!res.ok) return { success: false, error: json.error || '取消失败' };
  return json;
}

export async function registerLabActivity(activityId: string): Promise<ApiResponse<LabActivityRegistrationItem>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/lab-activities/${activityId}/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  });
  const json = await res.json();
  if (!res.ok) return { success: false, error: json.error || '报名失败' };
  return json;
}

export async function getLabActivityRegistrations(
  activityId: string,
  status?: 'pending' | 'approved' | 'rejected'
): Promise<ApiResponse<LabActivityRegistrationItem[]>> {
  const token = localStorage.getItem('token');
  const q = status ? `?status=${status}` : '';
  const res = await fetch(`${API_BASE}/api/lab-activities/${activityId}/registrations${q}`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
  });
  const json = await res.json();
  if (!res.ok) return { success: false, error: json.error || '获取报名列表失败' };
  return json;
}

export async function reviewLabActivityRegistration(
  registrationId: string,
  action: 'approve' | 'reject',
  rejectReason?: string
): Promise<ApiResponse<LabActivityRegistrationItem>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/lab-activities/registrations/${registrationId}/review`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ action, rejectReason })
  });
  const json = await res.json();
  if (!res.ok) return { success: false, error: json.error || '操作失败' };
  return json;
}

export async function cancelLabActivityRegistration(registrationId: string): Promise<ApiResponse<unknown>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/lab-activities/registrations/${registrationId}`, {
    method: 'DELETE',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
  });
  const json = await res.json();
  if (!res.ok) return { success: false, error: json.error || '取消失败' };
  return json;
}

export async function getMyLabActivityRegistrations(): Promise<ApiResponse<LabActivityRegistrationItem[]>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/lab-activities/my-registrations`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
  });
  const json = await res.json();
  if (!res.ok) return { success: false, error: json.error || '获取失败' };
  return json;
}

export interface LabTeamParticipationActivityRef {
  activityId: string;
  title: string;
  startTime: string;
  activityStatus: string;
}

export interface LabTeamParticipationRow {
  userId: string;
  name: string;
  studentId: string;
  studentType: string;
  /** 头像路径或 URL，与成员资料一致 */
  avatar?: string;
  approvedCount: number;
  pendingCount: number;
  rejectedCount: number;
  approvedActivities: LabTeamParticipationActivityRef[];
  approvedTitlesSummary: string;
  /** 已通过活动中最近一场的开始时间（ISO） */
  lastParticipatedAt?: string | null;
}

/** 团队参与统计（已加入团队的成员可查；超级管理员不可用） */
export async function getLabTeamParticipation(): Promise<
  ApiResponse<{ rows: LabTeamParticipationRow[]; generatedAt: string }>
> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/lab-activities/team-participation`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
  });
  const json = await res.json();
  if (!res.ok) return { success: false, error: json.error || '获取统计失败' };
  return json;
}

export interface LabTeamParticipationExportOptions {
  startDate?: string;
  endDate?: string;
}

/** 导出团队参与统计 Excel（租户管理员或团队负责人/管理员；超级管理员不可用） */
export async function downloadLabTeamParticipationExport(
  options?: LabTeamParticipationExportOptions
): Promise<void> {
  const token = localStorage.getItem('token');
  const params = new URLSearchParams();
  if (options?.startDate) params.set('startDate', options.startDate);
  if (options?.endDate) params.set('endDate', options.endDate);
  const qs = params.toString();
  const res = await fetch(
    `${API_BASE}/api/lab-activities/team-participation/export${qs ? `?${qs}` : ''}`,
    {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
    }
  );
  if (!res.ok) {
    let msg = '导出失败';
    try {
      const j = await res.json();
      if (j && j.error) msg = typeof j.error === 'string' ? j.error : msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const today = new Date().toISOString().slice(0, 10);
  const rangeSuffix =
    options?.startDate && options?.endDate ? `_${options.startDate}_${options.endDate}` : '';
  a.download = `团队活动参与统计${rangeSuffix}_${today}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/** 平台超级管理：团队列表项 */
export interface PlatformTeam {
  _id: string;
  name: string;
  code: string;
  status: 'active' | 'suspended';
  planTier?: 'free' | 'paid';
  ownerUserId?: string | null;
  adminEmail?: string;
  subscriptionEndsAt?: string | null;
  planLabel?: string;
  memberCount?: number;
  createdAt?: string;
  updatedAt?: string;
  /** 创建团队向导填写的注册表单字段 */
  category?: string | null;
  orgName?: string;
  teamSize?: string;
  ownerPosition?: string;
  industry?: string;
  researchField?: string;
  intro?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
}

/** 租户当前团队（团队管理页） */
export interface TenantTeamDetail {
  _id: string;
  name: string;
  code: string;
  status: 'active' | 'suspended';
  planTier?: 'free' | 'paid';
  subscriptionEndsAt?: string | null;
  planLabel?: string;
  category?: string | null;
  orgName?: string;
  teamSize?: string;
  ownerPosition?: string;
  industry?: string;
  researchField?: string;
  intro?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
}

export async function fetchCurrentTeam(): Promise<{
  team: TenantTeamDetail;
  memberCount: number;
  freeTierMaxMembers: number;
}> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/auth/team/current`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
  });
  const json = await res.json();
  if (!res.ok) {
    throw new Error((json as { error?: string }).error || '获取团队信息失败');
  }
  return json as { team: TenantTeamDetail; memberCount: number; freeTierMaxMembers: number };
}

export async function patchCurrentTeam(
  body: Partial<{
    name: string;
    category: string | null;
    orgName: string;
    teamSize: string;
    ownerPosition: string;
    industry: string;
    researchField: string;
    intro: string;
    contactName: string;
    contactPhone: string;
    contactEmail: string;
  }>
): Promise<{ success: boolean; team?: TenantTeamDetail; error?: string }> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/auth/team/current`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(body)
  });
  const json = await res.json();
  if (!res.ok) {
    return { success: false, error: (json as { error?: string }).error || '更新失败' };
  }
  return json as { success: boolean; team?: TenantTeamDetail };
}

/** 平台：未归属团队的用户 */
export interface PlatformUnassignedUser {
  _id: string;
  username: string;
  name?: string;
  email?: string;
  studentId?: string;
  role?: string;
  teamRole?: string;
  createdAt?: string;
  updatedAt?: string;
}

export async function fetchSuperadminUnassignedUsers(): Promise<
  ApiResponse<PlatformUnassignedUser[]>
> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/superadmin/unassigned-users`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
  });
  const json = await res.json();
  if (!res.ok) return { success: false, error: json.error || '获取列表失败' };
  return json;
}

export async function fetchSuperadminTeams(): Promise<ApiResponse<PlatformTeam[]>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/superadmin/teams`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
  });
  const json = await res.json();
  if (!res.ok) return { success: false, error: json.error || '获取团队列表失败' };
  return json;
}

export async function patchSuperadminTeam(
  id: string,
  body: {
    name?: string;
    status?: 'active' | 'suspended';
    planTier?: 'free' | 'paid';
    subscriptionEndsAt?: string | null;
    planLabel?: string;
  }
): Promise<ApiResponse<PlatformTeam>> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/superadmin/teams/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(body)
  });
  const json = await res.json();
  if (!res.ok) return { success: false, error: json.error || '更新失败' };
  return json;
}
